import type React from 'react';
import {useMemo, useState} from 'react';
import {Box, Button, Link} from '@mui/material';
import {Add} from '@mui/icons-material';
import type {GridColDef} from '@mui/x-data-grid-premium';
import {CustomDataGrid} from '~/components/data-grid/CustomDataGrid';
import type {RecordActions} from '../../hooks/useRecordActions';
import type {CategoryRecord, TeamMember} from '../../types/categoryRecordTypes';
import {ProfileSection} from '../components/ProfileSection';
import {PersonDialog} from './PersonDialog';
import {RowAction} from '../components/RowAction';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';

type Props = {record: CategoryRecord; actions: RecordActions};

/** Team members (spec 4.4): users of the app, with Name · Role · RACI · Contact (the user's) · Remove (with Undo).
 * Role and RACI edit in place. */
export const TeamSection: React.FC<Props> = ({record, actions}) => {
    const [adding, setAdding] = useState(false);
    const {canEdit, options} = record;

    const columns: GridColDef<TeamMember>[] = useMemo(() => [
        {field: 'name', headerName: 'Name', flex: 1, minWidth: 180,
            valueFormatter: (v: string, r) => r.id === record.ownerId ? `${v} (owner)` : v},
        {field: 'role', headerName: 'Role', width: 190, type: 'singleSelect', valueOptions: options.roles, editable: canEdit},
        {field: 'raci', headerName: 'RACI', width: 130, type: 'singleSelect', valueOptions: options.raci,
            editable: canEdit},
        {field: 'email', headerName: 'Contact', flex: 1, minWidth: 200, sortable: false,
            renderCell: ({value}) => value ? <Link href={`mailto:${value}`} underline="hover">{value}</Link> : '—'},
        ...(canEdit ? [{
            field: 'remove', headerName: '', width: 64, sortable: false, filterable: false, disableColumnMenu: true,
            align: 'center',
            renderCell: ({row}) => (
                <RowAction kind="delete" label={row.name} onClick={() => void actions.removeTeamMember(row).catch(() => undefined)}/>
            ),
        } satisfies GridColDef<TeamMember>] : []),
    ], [canEdit, options, record.ownerId, actions]);

    return (
        <ProfileSection help={OVERVIEW_HELP.team}
                        title={`Team members (${record.team.length})`}
                        actions={canEdit && <Button size="small" startIcon={<Add/>} onClick={() => setAdding(true)}>Add team member</Button>}
        >
            {/* Rows (36px) for up to one page, plus header and pagination bar */}
            <Box sx={{height: Math.max(2, Math.min(record.team.length, 10)) * 36 + 100}}>
                <CustomDataGrid
                    layoutKey="category-profile-team"
                    rows={record.team} columns={columns} getRowId={r => r.id}
                    initialState={{pagination: {paginationModel: {pageSize: 10, page: 0}}}}
                    disableRowSelectionOnClick
                    processRowUpdate={async (next: TeamMember, prev: TeamMember) => {
                        const change = (['role', 'raci'] as const)
                            .filter(k => next[k] !== prev[k] && String(next[k]).trim() !== '')
                            .reduce((acc, k) => ({...acc, [k]: next[k]}), {});
                        if (Object.keys(change).length === 0) return prev;
                        await actions.updateTeamMember(prev, change);
                        return next;
                    }}
                    onProcessRowUpdateError={() => undefined}
                    localeText={{noRowsLabel: canEdit ? 'No team members yet. Add the people who work on this category.' : 'No team members yet.'}}
                />
            </Box>
            {adding && (
                <PersonDialog
                    title="Add team member" submitLabel="Add"
                    roles={options.roles} raci={options.raci} defaultRole="Business stakeholder"
                    onSubmit={(input, name) => actions.addTeamMember(input, name)}
                    onClose={() => setAdding(false)}
                />
            )}
        </ProfileSection>
    );
};
